import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { themeStateRepository } from "@/infrastructure/repositories/storage/ThemeState.repository";
import {
  DEFAULT_THEME_PREFERENCE,
  THEME_STORAGE_FULL_KEY,
  applyResolvedTheme,
  getSystemTheme,
  isThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from "@/core/theme/theme";

interface ThemeContextValue {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_FULL_KEY);
    if (!raw) return DEFAULT_THEME_PREFERENCE;
    const parsed = JSON.parse(raw) as { value?: unknown };
    return isThemePreference(parsed.value) ? parsed.value : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

function ThemeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [preference, setPreference] = useState<ThemePreference>(() => readStoredPreference());
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());
  const resolvedTheme = preference === "system" ? systemTheme : preference;

  useEffect(() => {
    applyResolvedTheme(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemTheme(getSystemTheme());
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const persistPreference = useCallback((next: ThemePreference) => {
    setPreference(next);
    void themeStateRepository.setPreference(next);
  }, []);

  const value = useMemo(
    () => ({ preference, resolvedTheme, setPreference: persistPreference }),
    [preference, resolvedTheme, persistPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export default ThemeProvider;
